import { useState } from 'react';
import { Icon } from '../common';

// The On-duty card's copy control (Freddi, 2026-07-24). Deliberately quiet: an
// icon-led ghost button in the card head that reads as part of the card, not a
// call to action.
//
// Confirmation is the LABEL AND ICON — "Copy" → "Copied", the copy glyph → a
// tick, for 2.2s, then back. What it deliberately does NOT do is change fill or
// colour (Freddi, 2026-07-24): the Messages page's filled-green treatment is
// right for a primary button on its own pane and far too loud for a control
// tucked into a card header. Hover is the only thing that tints this button.
// The screen-reader status carries the same confirmation for anyone who can't
// see either change.
//
// Owns its own clipboard + transient state so the card stays presentational.
//
// `text` is a THUNK, not a string: the message is only needed at the moment of
// the click, and building it eagerly would read `window.location.origin` during
// render — which crashes the SSR unit harness (renderToStaticMarkup runs in
// node, where `window` is undefined) and rebuilds the string on every render
// for nothing. Lazy is both SSR-safe and cheaper.
export function CopyBriefButton({
  text,
  title,
  disabled = false,
  disabledTitle,
}: {
  text: () => string;
  title: string;
  // Set while the underlying figures cannot be trusted (a day-step still
  // loading) or the message would not make sense (a day other than today).
  // Publishing a wrong count to the whole staff group is worse than a control
  // that greys out for a moment.
  disabled?: boolean;
  disabledTitle?: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      // Clipboard denied (rare, permissioned contexts). Leave the button as is
      // rather than faking success — the same call the Messages page makes.
    }
  };

  return (
    <>
      <button
        type="button"
        // `is-copied` is an ANIMATION HOOK ONLY — it carries no fill or colour
        // (see the CSS); it exists so the tick can spring in on the swap.
        className={'today-ctx-copy' + (copied ? ' is-copied' : '')}
        onClick={copy}
        disabled={disabled}
        // title tracks the live state — a stale tooltip over a button reading
        // "Copied" contradicts both the label and the SR announcement.
        title={disabled ? (disabledTitle ?? title) : copied ? 'Message copied to clipboard' : title}
        aria-label={disabled ? (disabledTitle ?? title) : copied ? 'Message copied to clipboard' : title}
      >
        <Icon name={copied ? 'check' : 'copy'} size={13} />
        <span>{copied ? 'Copied' : 'Copy'}</span>
      </button>
      {/* The tick + colour shift is the sighted cue; this announces the same. */}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? 'Message copied to clipboard' : ''}
      </span>
    </>
  );
}
